import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';

import {
    BaseQuestionIdentifier,
    CaseResource,
    CaseResourceIdentifier,
    ConditionDisclosureResource,
    ConditionsResource,
    ConditionsResourceIdentifier,
    DisclosurePhysicianIdentifier,
    DisclosurePhysicianResource,
    InterviewDisclosureIdentifier,
    InterviewResourceIdentifier,
    LifeResourceIdentifier,
    ListItemResource,
    PhysicianResource,
    PhysicianResourceIdentifier,
    PrimaryPhysicianResource,
    QuestionPagesResource,
    ScreensQuestionResource
} from '@Components/mras/core';
import * as _ from 'lodash';
import { IConfigService } from '@Config';
import { P } from '@angular/cdk/keycodes';
import { AppService, InterviewService, MonitoringService } from '@App';
import { NotificationService, NotificationSeverity } from '@Services';
import { FeatureToggle } from '@App/enums/feature-toggle.enum';
import { FeatureManagerService } from 'app/services/global/feature-manager/feature-manager.service';

@Injectable({ providedIn: 'root' })
export class AisRestService{

    apiUrl: string;
    private _activeEnvironment: string;
    private readonly genericRetryMessage = 'Something went wrong. Please try again.';

    set activeEnvironment(val: string) {
        this._activeEnvironment = val;
    }

    private static replaceUrlCharacters(elem: string): string {
        const DOT = "*dot*";
        const SLASH = "*slash*";
        if (elem == null) {
            return null;
        }
        return elem.replace(/\//g, SLASH).replace(/\./g, DOT);
    }

    constructor(
        public httpClient: HttpClient,
        public configService: IConfigService,
        public appService: AppService,
        public notificationService: NotificationService,
        public interviewService: InterviewService,
        public monitorService: MonitoringService,
        private readonly featureManagerService: FeatureManagerService
    ) {
        
        this.apiUrl =  this.configService.getMrasApiUrl();
        
    }
    
    private getUserActionProperties(questionReference: string): { [key: string]: string } | null {
        const caseDetails = this.interviewService.getInterviewData().caseDetails;
        if (!caseDetails) {
            return null;
        }

        return {
            "sectionName": this.interviewService.getActiveSection().name,
            "caseNumber": caseDetails.caseNumber,
            "accountCode": this.interviewService.account.code,
            "role": this.appService.getUserRoleName(),
            "questionReference": questionReference,
            "caseCreatedDate": caseDetails.creationDate
        };
    }

    // Starts (or joins) the user action when the request is subscribed, so the request belongs to it.
    private trackUserAction<T>(questionReference: string, request: Observable<T>): Observable<T> {
        return new Observable<T>(subscriber => this.monitorService.runAction(`Answered Question`, this.getUserActionProperties(questionReference), () => {
            return request.subscribe(subscriber);
        }));
    }

    private getOriginalErrorValue(err: any): any {
        return err?.error?.details || err?.error?.message || err?.error || err?.message || this.genericRetryMessage;
    }

    isClearFailedMrasAnswersEnabled(): boolean {
        return this.featureManagerService.getByName(FeatureToggle.CaseInterviewClearFailedMrasAnswers).enabled;
    }

    private handleMutationError(err: any): Observable<never> {
        if (!this.isClearFailedMrasAnswersEnabled()) {
            const errorDetails = JSON.parse(err.error.details);
            this.notificationService.showNotification({ severity: NotificationSeverity.Error, message: errorDetails.message });
            return throwError(() => new Error(err.error.details));
        }

        const originalError = this.getOriginalErrorValue(err);
        this.notificationService.showNotification({ severity: NotificationSeverity.Error, message: this.genericRetryMessage });
        return throwError(() => originalError);
    }

    getCase(identifier: CaseResourceIdentifier): Observable<CaseResource> {
        const caseId = AisRestService.replaceUrlCharacters(identifier.caseId);
        const path = `${this.apiUrl}/api/${this._activeEnvironment}/cases/${caseId}`;
        this.appService.display(true);
        return this.httpClient.get<CaseResource>(path, { headers: this.appService.getHeaders()}).pipe(
            tap(e => console.log(`[REST] get case info\nurl: ${path}\nresponse:`, e)),
            catchError(err => {
                const errorDetails = JSON.parse(err.error.details);
                this.notificationService.showNotification({ severity: NotificationSeverity.Error, message: errorDetails.message });
                throw new Error(err.error.details);
            }),
            finalize(() => {this.appService.display(false);})
        );
    }

    getBaseQuestion(identifier: BaseQuestionIdentifier): Observable<QuestionPagesResource> {
        const caseId = AisRestService.replaceUrlCharacters(identifier.caseId);
        const lifeInstance = identifier.lifeInstance;
        const interviewCode = AisRestService.replaceUrlCharacters(identifier.interviewCode);
        const baseQuestionCode = AisRestService.replaceUrlCharacters(identifier.baseQuestionCode);
        let path = `${this.apiUrl}/api/${this._activeEnvironment}/cases/${caseId}/lives/${lifeInstance}/interviews/${interviewCode}/questions/${baseQuestionCode}?expand=true`;
        if (!_.isEmpty(identifier.interviewInstance)) {
            path += `&interviewInstance=${identifier.interviewInstance}`;
        }
        this.appService.display(true);
        return this.httpClient.get<QuestionPagesResource>(path, { headers: this.appService.getHeaders()}).pipe(
            tap(e => console.log(`[REST] get base questions in hierarchy\nurl: ${path}\nresponse:`, e)),
            catchError(err => {
                const errorDetails = JSON.parse(err.error.details);
                this.notificationService.showNotification({ severity: NotificationSeverity.Error, message: errorDetails.message });
                throw new Error(err.error.details);
            }),
            finalize(() => {this.appService.display(false);})
        );
    }

    getBaseQuestionsInHierarchy(identifier: InterviewResourceIdentifier): Observable<QuestionPagesResource> {
        const caseId = AisRestService.replaceUrlCharacters(identifier.caseId);
        const lifeInstance = identifier.lifeInstance;
        const interviewCode = AisRestService.replaceUrlCharacters(identifier.interviewCode);
        let path = `${this.apiUrl}/api/${this._activeEnvironment}/cases/${caseId}/lives/${lifeInstance}/interviews/${interviewCode}/questions/hierarchical?expand=true`;
        if (!_.isEmpty(identifier.interviewInstance)) {
            path += `&interviewInstance=${identifier.interviewInstance}`;
        }
        this.appService.display(true);
        return this.httpClient.get<QuestionPagesResource>(path, { headers: this.appService.getHeaders()}).pipe(
            tap(e => console.log(`[REST] get base questions in hierarchy\nurl: ${path}\nresponse:`, e)),
            catchError(err => {
                const errorDetails = JSON.parse(err.error.details);
                this.notificationService.showNotification({ severity: NotificationSeverity.Error, message: errorDetails.message });
                throw new Error(err.error.details);
            }),
            finalize(() => {this.appService.display(false);})
        );
    }

    getDisclosuresInInterview(identifier: InterviewResourceIdentifier): Observable<ConditionDisclosureResource> {
        const caseId = AisRestService.replaceUrlCharacters(identifier.caseId);
        const lifeInstance = identifier.lifeInstance;
        const interviewCode = AisRestService.replaceUrlCharacters(identifier.interviewCode);
        let path = `${this.apiUrl}/api/${this._activeEnvironment}/cases/${caseId}/lives/${lifeInstance}/interviews/${interviewCode}/disclosures`;
        if (!_.isEmpty(identifier.interviewInstance)) {
            path += `&interviewInstance=${identifier.interviewInstance}`;
        }
        this.appService.display(true);
        return this.httpClient.get<ConditionDisclosureResource>(path, { headers: this.appService.getHeaders()}).pipe(
            tap(e => console.log(`[REST] get disclosures in interview\nurl: ${path}\nresponse:`, e)),
            catchError(err => {
                const errorDetails = JSON.parse(err.error.details);
                this.notificationService.showNotification({ severity: NotificationSeverity.Error, message: errorDetails.message });
                throw new Error(err.error.details);
            }),
            finalize(() => {this.appService.display(false);})
        );
    }

    answerBaseQuestion(identifier: BaseQuestionIdentifier, answer: ScreensQuestionResource): Observable<ScreensQuestionResource> {
        const caseId = AisRestService.replaceUrlCharacters(identifier.caseId);
        const lifeInstance = identifier.lifeInstance;
        const interviewCode = AisRestService.replaceUrlCharacters(identifier.interviewCode);
        const baseQuestionCode = AisRestService.replaceUrlCharacters(identifier.baseQuestionCode);
        let path = `${this.apiUrl}/api/${this._activeEnvironment}/cases/${caseId}/lives/${lifeInstance}/interviews/${interviewCode}/questions/${baseQuestionCode}?expand=true`;
        if (!_.isEmpty(identifier.interviewInstance)) {
            path += `&interviewInstance=${identifier.interviewInstance}`;
        }
        this.appService.display(true);

        return this.trackUserAction(baseQuestionCode, this.httpClient.patch<ScreensQuestionResource>(path, answer, { headers: this.appService.getHeaders()}).pipe(
            tap(e => console.log(`[REST] answer base question\nurl: ${path}\nresponse:`, e)),
            catchError(err => this.handleMutationError(err)),
            finalize(() => {this.appService.display(false);})
        ));
    }

    answerReflexiveQuestion(
        identifier: InterviewDisclosureIdentifier,
        answer: ConditionDisclosureResource
    ): Observable<ConditionDisclosureResource> {
        const caseId = AisRestService.replaceUrlCharacters(identifier.caseId);
        const lifeInstance = identifier.lifeInstance;
        const interviewCode = AisRestService.replaceUrlCharacters(identifier.interviewCode);
        const baseQuestionCode = AisRestService.replaceUrlCharacters(identifier.baseQuestionCode);        
        const disclosureName = AisRestService.replaceUrlCharacters(identifier.aliasName);

        let path = `${this.apiUrl}/api/${this._activeEnvironment}/cases/${caseId}/lives/${lifeInstance}/interviews/${interviewCode}/baseQuestions/${baseQuestionCode}/disclosures/${disclosureName}?expand=true&interviewInstance=`;
        if (!_.isEmpty(identifier.interviewInstance)) {
            path +=identifier.interviewInstance;
        }

        this.appService.display(true);
        return this.trackUserAction(`${baseQuestionCode}-${disclosureName}`, this.httpClient.patch<ConditionDisclosureResource>(path, answer, { headers: this.appService.getHeaders()}).pipe(
            tap(e => console.log(`[REST] answer reflexive question\nurl: ${path}\nresponse:`, e)),
            catchError(err => {
                if (!this.isClearFailedMrasAnswersEnabled()) {
                    return this.handleMutationError(err);
                }

                return throwError(() => err?.error?.details || err?.message || this.genericRetryMessage);
            }),
            finalize(() => {this.appService.display(false);})
        ));
    }

    createDisclosure(identifier: BaseQuestionIdentifier, disclosure: ConditionDisclosureResource): Observable<ConditionDisclosureResource> {
        const caseId = AisRestService.replaceUrlCharacters(identifier.caseId);
        const lifeInstance = identifier.lifeInstance;
        const interviewCode = AisRestService.replaceUrlCharacters(identifier.interviewCode);
        const baseQuestionCode = AisRestService.replaceUrlCharacters(identifier.baseQuestionCode);
        const path = `${this.apiUrl}/api/${this._activeEnvironment}/cases/${caseId}/lives/${lifeInstance}/interviews/${interviewCode}/baseQuestions/${baseQuestionCode}/disclosures?expand=true`;
        this.appService.display(true);

        return this.trackUserAction(`${baseQuestionCode}-${disclosure.aliasName}`, this.httpClient.post<ConditionDisclosureResource>(path, disclosure, { headers: this.appService.getHeaders()}).pipe(
            tap(e => console.log(`[REST] create disclosure\nurl: ${path}\nresponse:`, e)),
            catchError(err => this.handleMutationError(err)),
            finalize(() => {this.appService.display(false);})
        ));
    }

    updateDisclosure(
        identifier: InterviewDisclosureIdentifier,
        disclosure: ConditionDisclosureResource
    ): Observable<ConditionDisclosureResource> {
        const caseId = AisRestService.replaceUrlCharacters(identifier.caseId);
        const lifeInstance = identifier.lifeInstance;
        const interviewCode = AisRestService.replaceUrlCharacters(identifier.interviewCode);
        const baseQuestionCode = AisRestService.replaceUrlCharacters(identifier.baseQuestionCode);
        const disclosureName = AisRestService.replaceUrlCharacters(identifier.aliasName);
        const path = `${this.apiUrl}/api/${this._activeEnvironment}/cases/${caseId}/lives/${lifeInstance}/interviews/${interviewCode}/baseQuestions/${baseQuestionCode}/disclosures/${disclosureName}?expand=true&interviewInstance=`;
        this.appService.display(true);
        return this.trackUserAction(`${baseQuestionCode}-${disclosureName}`, this.httpClient.patch<ConditionDisclosureResource>(path, disclosure, { headers: this.appService.getHeaders()}).pipe(
            tap(e => console.log(`[REST] update disclosure\nurl: ${path}\nresponse:`, e)),
            catchError(err => this.handleMutationError(err)),
            finalize(() => {this.appService.display(false);})
        ));
    }

    deleteDisclosure(identifier: InterviewDisclosureIdentifier): Observable<ConditionDisclosureResource> {
        const caseId = AisRestService.replaceUrlCharacters(identifier.caseId);
        const lifeInstance = identifier.lifeInstance;
        const interviewCode = AisRestService.replaceUrlCharacters(identifier.interviewCode);
        const baseQuestionCode = AisRestService.replaceUrlCharacters(identifier.baseQuestionCode);
        const disclosureName = AisRestService.replaceUrlCharacters(identifier.aliasName);
        const path = `${this.apiUrl}/api/${this._activeEnvironment}/cases/${caseId}/lives/${lifeInstance}/interviews/${interviewCode}/baseQuestions/${baseQuestionCode}/disclosures/${disclosureName}?expand=true`;
        this.appService.display(true);

        return this.trackUserAction(`remove answer: ${baseQuestionCode}-${disclosureName}`, this.httpClient.delete<ConditionDisclosureResource>(path, { headers: this.appService.getHeaders() }).pipe(
            tap(e => console.log(`[REST] delete disclosure\nurl: ${path}\nresponse:`, e)),
            catchError(err => {
                // TODO: Might need to revisit later. To Suppress known MRAS 405 response for PT2_ALCOHOL_1 disclosure delete.
                if (this.isClearFailedMrasAnswersEnabled() &&
                    err?.status === 405 &&
                    identifier.baseQuestionCode === 'PT2_ALCOHOL_1') {
                    return of({} as ConditionDisclosureResource);
                }

                return this.handleMutationError(err);
            }),
            finalize(() => {this.appService.display(false);})
        ));
    }

    getDisclosure(identifier: InterviewDisclosureIdentifier): Observable<ConditionDisclosureResource> {
        const caseId = AisRestService.replaceUrlCharacters(identifier.caseId);
        const lifeInstance = identifier.lifeInstance;
        const interviewCode = AisRestService.replaceUrlCharacters(identifier.interviewCode);
        const baseQuestionCode = AisRestService.replaceUrlCharacters(identifier.baseQuestionCode);
        const disclosureName = AisRestService.replaceUrlCharacters(identifier.aliasName);
        let path = `${this.apiUrl}/api/${this._activeEnvironment}/cases/${caseId}/lives/${lifeInstance}/interviews/${interviewCode}/baseQuestions/${baseQuestionCode}/disclosures/${disclosureName}?expand=true`;
        if (!_.isEmpty(identifier.interviewInstance)) {
            path += `&interviewInstance=${identifier.interviewInstance}`;
        }
        this.appService.display(true);
        return this.httpClient.get<ConditionDisclosureResource>(path, { headers: this.appService.getHeaders()}).pipe(
            tap(e => console.log(`[REST] get disclosure\nurl: ${path}\nresponse:`, e)),
            catchError(err => {
                const errorDetails = JSON.parse(err.error.details);
                this.notificationService.showNotification({ severity: NotificationSeverity.Error, message: errorDetails.message });
                throw new Error(err.error.details);
            }),
            finalize(() => {this.appService.display(false);})
        );
    }

    searchDisclosure(identifier: ConditionsResourceIdentifier, term: string): Observable<ConditionsResource> {
        const caseId = AisRestService.replaceUrlCharacters(identifier.caseId);
        const lifeInstance = identifier.lifeInstance;
        const interviewCode = AisRestService.replaceUrlCharacters(identifier.interviewCode);
        const categoryCode = AisRestService.replaceUrlCharacters(identifier.categoryCode);
        const limit = identifier.limit;
        const path = `${this.apiUrl}/api/${this._activeEnvironment}/cases/${caseId}/lives/${lifeInstance}/interviews/${interviewCode}/conditions/${categoryCode}?searchTerm=${term}&limit=${limit}`;
        this.appService.display(true);
        return this.httpClient.get<ConditionsResource>(path, { headers: this.appService.getHeaders()}).pipe(
            tap(e => console.log(`[REST] search disclosure\nurl: ${path}\nresponse:`, e)),
            catchError(err => {
                const errorDetails = JSON.parse(err.error.details);
                this.notificationService.showNotification({ severity: NotificationSeverity.Error, message: errorDetails.message });
                throw new Error(err.error.details);
            }),
            finalize(() => {this.appService.display(false);})
        );
    }

    createPhysician(identifier: CaseResourceIdentifier, physician: PhysicianResource): Observable<PhysicianResource> {
        const caseId = AisRestService.replaceUrlCharacters(identifier.caseId);
        const path = `${this.apiUrl}/api/${this._activeEnvironment}/cases/${caseId}/physicians`;
        this.appService.display(true);

        return this.trackUserAction(`Create Physician`, this.httpClient.post<PhysicianResource>(path, physician, { headers: this.appService.getHeaders()}).pipe(
            tap(e => console.log(`[REST] create physician\nurl: ${path}\nresponse:`, e)),
            catchError(err => {
                const errorDetails = JSON.parse(err.error.details);
                this.notificationService.showNotification({ severity: NotificationSeverity.Error, message: errorDetails.message });
                throw new Error(err.error.details);
            }),
            finalize(() => {this.appService.display(false);})
        ));
    }

    updatePhysician(identifier: PhysicianResourceIdentifier, physician: PhysicianResource): Observable<PhysicianResource> {
        const caseId = AisRestService.replaceUrlCharacters(identifier.caseId);
        const uuid = identifier.uuid;
        const path = `${this.apiUrl}/api/${this._activeEnvironment}/cases/${caseId}/physicians/${uuid}`;
        this.appService.display(true);
        
        return this.trackUserAction(`Update Physician`, this.httpClient.patch<PhysicianResource>(path, physician, { headers: this.appService.getHeaders()}).pipe(
            tap(e => console.log(`[REST] update physician\nurl: ${path}\nresponse:`, e)),
            catchError(err => {
                const errorDetails = JSON.parse(err.error.details);
                this.notificationService.showNotification({ severity: NotificationSeverity.Error, message: errorDetails.message });
                throw new Error(err.error.details);
            }),
            finalize(() => {this.appService.display(false);})
        ));
    }

    deletePhysician(identifier: PhysicianResourceIdentifier) {
        const caseId = AisRestService.replaceUrlCharacters(identifier.caseId);
        const uuid = identifier.uuid;
        const path = `${this.apiUrl}/api/${this._activeEnvironment}/cases/${caseId}/physicians/${uuid}`;
        this.appService.display(true);
        
        return this.trackUserAction(`Delete Physician`, this.httpClient.delete<PhysicianResource>(path, { headers: this.appService.getHeaders()}).pipe(
            tap(e => console.log(`[REST] delete physician\nurl: ${path}\nresponse:`, e)),
            catchError(err => {
                const errorDetails = JSON.parse(err.error.details);
                this.notificationService.showNotification({ severity: NotificationSeverity.Error, message: errorDetails.message });
                throw new Error(err.error.details);
            }),
            finalize(() => {this.appService.display(false);})
        ));
    }

    createDisclosurePhysician(identifier: InterviewDisclosureIdentifier, physicianDisclosure: DisclosurePhysicianResource) {
        const caseId = AisRestService.replaceUrlCharacters(identifier.caseId);
        const lifeInstance = identifier.lifeInstance;
        const interviewCode = AisRestService.replaceUrlCharacters(identifier.interviewCode);
        const aliasName = AisRestService.replaceUrlCharacters(identifier.aliasName);
        const path = `${this.apiUrl}/api/${this._activeEnvironment}/cases/${caseId}/lives/${lifeInstance}/interviews/${interviewCode}/disclosures/${aliasName}/physicians`;
        this.appService.display(true);
        
        return this.trackUserAction(`Create Physician Disclosure for ${aliasName}`, this.httpClient.post<PhysicianResource>(path, physicianDisclosure, { headers: this.appService.getHeaders()}).pipe(
            tap(e => console.log(`[REST] create disclosure physician\nurl: ${path}\nresponse:`, e)),
            catchError(err => {
                const errorDetails = JSON.parse(err.error.details);
                this.notificationService.showNotification({ severity: NotificationSeverity.Error, message: errorDetails.message });
                throw new Error(err.error.details);
            }),
            finalize(() => {this.appService.display(false);})
        ));
    }

    updateDisclosurePhysician(identifier: DisclosurePhysicianIdentifier, resource: DisclosurePhysicianResource) {
        const caseId = AisRestService.replaceUrlCharacters(identifier.caseId);
        const lifeInstance = identifier.lifeInstance;
        const interviewCode = AisRestService.replaceUrlCharacters(identifier.interviewCode);
        const aliasName = AisRestService.replaceUrlCharacters(identifier.aliasName);
        const uuid = identifier.physicianUuid;
        const path = `${this.apiUrl}/api/${this._activeEnvironment}/cases/${caseId}/lives/${lifeInstance}/interviews/${interviewCode}/disclosures/${aliasName}/physicians/${uuid}`;
        this.appService.display(true);
        
        return this.trackUserAction(`Update Physician Disclosure for ${aliasName}`, this.httpClient.patch<PhysicianResource>(path, resource, { headers: this.appService.getHeaders()}).pipe(
            tap(e => console.log(`[REST] update disclosure physician\nurl: ${path}\nresponse:`, e)),
            catchError(err => {
                const errorDetails = JSON.parse(err.error.details);
                this.notificationService.showNotification({ severity: NotificationSeverity.Error, message: errorDetails.message });
                throw new Error(err.error.details);
            }),
            finalize(() => {this.appService.display(false);})
        ));
    }

    deleteDisclosurePhysician(identifier: DisclosurePhysicianIdentifier) {
        const caseId = AisRestService.replaceUrlCharacters(identifier.caseId);
        const lifeInstance = identifier.lifeInstance;
        const interviewCode = AisRestService.replaceUrlCharacters(identifier.interviewCode);
        const aliasName = AisRestService.replaceUrlCharacters(identifier.aliasName);
        const uuid = identifier.physicianUuid;
        const path = `${this.apiUrl}/api/${this._activeEnvironment}/cases/${caseId}/lives/${lifeInstance}/interviews/${interviewCode}/disclosures/${aliasName}/physicians/${uuid}`;
        this.appService.display(true);
        return this.trackUserAction(`Delete Physician Disclosure for ${aliasName}`, this.httpClient.delete<PhysicianResource>(path, { headers: this.appService.getHeaders()}).pipe(
            tap(e => console.log(`[REST] delete disclosure physician\nurl: ${path}\nresponse:`, e)),
            catchError(err => {
                const errorDetails = JSON.parse(err.error.details);
                this.notificationService.showNotification({ severity: NotificationSeverity.Error, message: errorDetails.message });
                throw new Error(err.error.details);
            }),
            finalize(() => {this.appService.display(false);})
        ));
    }

    getPrimaryPhysician(identifier: LifeResourceIdentifier): Observable<PrimaryPhysicianResource> {
        const caseId = AisRestService.replaceUrlCharacters(identifier.caseId);
        const lifeInstance = identifier.lifeInstance;
        const path = `${this.apiUrl}/api/${this._activeEnvironment}/cases/${caseId}/lives/${lifeInstance}/primaryPhysician?expand=true`;
        this.appService.display(true);
        return this.httpClient.get<PrimaryPhysicianResource>(path, { headers: this.appService.getHeaders()}).pipe(
            tap(e => console.log(`[REST] get primary physician\nurl: ${path}\nresponse:`, e)),
            catchError(err => {
                const errorDetails = JSON.parse(err.error.details);
                this.notificationService.showNotification({ severity: NotificationSeverity.Error, message: errorDetails.message });
                throw new Error(err.error.details);
            }),
            finalize(() => {this.appService.display(false);})
        );
    }

    createPrimaryPhysician(identifier: LifeResourceIdentifier, resource: PrimaryPhysicianResource): Observable<PrimaryPhysicianResource> {
        const caseId = AisRestService.replaceUrlCharacters(identifier.caseId);
        const lifeInstance = identifier.lifeInstance;
        const path = `${this.apiUrl}/api/${this._activeEnvironment}/cases/${caseId}/lives/${lifeInstance}/primaryPhysician?expand=true`;
        this.appService.display(true);
        
        return this.trackUserAction(`Create Primary Physician`, this.httpClient.post<PrimaryPhysicianResource>(path, resource, { headers: this.appService.getHeaders()}).pipe(
            tap(e => console.log(`[REST] create primary physician\nurl: ${path}\nresponse:`, e)),
            catchError(err => {
                const errorDetails = JSON.parse(err.error.details);
                this.notificationService.showNotification({ severity: NotificationSeverity.Error, message: errorDetails.message });
                throw new Error(err.error.details);
            }),
            finalize(() => {this.appService.display(false);})
        ));
    }

    updatePrimaryPhysician(identifier: LifeResourceIdentifier, resource: PrimaryPhysicianResource): Observable<PrimaryPhysicianResource> {
        const caseId = AisRestService.replaceUrlCharacters(identifier.caseId);
        const lifeInstance = identifier.lifeInstance;
        const path = `${this.apiUrl}/api/${this._activeEnvironment}/cases/${caseId}/lives/${lifeInstance}/primaryPhysician?expand=true`;
        this.appService.display(true);
        return this.trackUserAction(`Update Primary Physician`, this.httpClient.patch<PrimaryPhysicianResource>(path, resource, { headers: this.appService.getHeaders()}).pipe(
            tap(e => console.log(`[REST] update primary physician\nurl: ${path}\nresponse:`, e)),
            catchError(err => {
                const errorDetails = JSON.parse(err.error.details);
                this.notificationService.showNotification({ severity: NotificationSeverity.Error, message: errorDetails.message });
                throw new Error(err.error.details);
            }),
            finalize(() => {this.appService.display(false);})
        ));
    }

    deletePrimaryPhysician(identifier: LifeResourceIdentifier) {
        const caseId = AisRestService.replaceUrlCharacters(identifier.caseId);
        const lifeInstance = identifier.lifeInstance;
        const path = `${this.apiUrl}/api/${this._activeEnvironment}/cases/${caseId}/lives/${lifeInstance}/primaryPhysician`;

        this.appService.display(true);
        return this.trackUserAction(`Delete Primary Physician`, this.httpClient.delete<void>(path, { headers: this.appService.getHeaders()}).pipe(
            tap(e => console.log(`[REST] delete primary physician\nurl: ${path}\nresponse:`, e)),
            catchError(err => {
                const errorDetails = JSON.parse(err.error.details);
                this.notificationService.showNotification({ severity: NotificationSeverity.Error, message: errorDetails.message });
                throw new Error(err.error.details);
            }),
            finalize(() => {this.appService.display(false);})
        ));
    }

    getStatesForCountry(identifier: CaseResourceIdentifier, countryCode: string): Observable<ListItemResource[]> {
        const caseId = AisRestService.replaceUrlCharacters(identifier.caseId);
        const path = `${this.apiUrl}/api/${this._activeEnvironment}/cases/${caseId}/statesForCountry/${countryCode}`;

        this.appService.display(true);
        return this.httpClient.get<ListItemResource[]>(path, { headers: this.appService.getHeaders()}).pipe(
            tap(e => console.log(`[REST] get states for country\nurl: ${path}\nresponse:`, e)),
            catchError(err => {
                const errorDetails = JSON.parse(err.error.details);
                this.notificationService.showNotification({ severity: NotificationSeverity.Error, message: errorDetails.message });
                throw new Error(err.error.details);
            }),
            finalize(() => {this.appService.display(false);})
        );
    }   
}
